'use client'

import React, { useState, useCallback, useEffect } from 'react'
import { PaginationBar } from './PaginationBar'
import { TableActionMenu, type ActionMenuItem } from './TableActionMenu'
import '@/styles/table.css'

interface Column {
  key: string
  label: React.ReactNode
  align?: 'left' | 'center' | 'right'
  width?: string
  className?: string
  rowSpan?: (row: Record<string, unknown>, index: number, rows: Record<string, unknown>[]) => number
  render?: (value: any, row: any, index?: number, context?: { openMenuRowId: string | number | null; setOpenMenuRowId: (id: string | number | null) => void }) => React.ReactNode
}

interface DragDropHandlers<T = any> {
  draggedItem: T | null
  dragOverIndex: number | null
  handleDragStart: (e: React.DragEvent, item: T, index: number) => void
  handleDragEnd: () => void
  handleDragOver: (e: React.DragEvent, index: number) => void
  handleDragLeave: () => void
  handleDrop: (e: React.DragEvent, index: number) => void
}

interface TableProps {
  columns: Column[]
  data: any[]
  loading?: boolean
  emptyMessage?: string
  rowKey?: string
  onRowClick?: (row: any) => void
  // Pagination
  pageSize?: number
  enablePagination?: boolean
  serverSidePagination?: boolean
  totalRecords?: number
  currentPage?: number
  onPageChange?: (page: number) => void
  // Drag & Drop
  dragDropHandlers?: DragDropHandlers
  draggable?: boolean
  // Custom row style
  getRowStyle?: (row: any, index: number) => React.CSSProperties
  getRowClassName?: (row: any, index: number) => string
  onRowMouseEnter?: (row: any, index: number) => void
  onRowMouseLeave?: (row: any, index: number) => void
  actions?: (row: any, index: number) => ActionMenuItem[]
}

export function Table({ 
  columns, 
  data, 
  loading = false, 
  emptyMessage = 'Kayıt bulunamadı',
  rowKey = 'id',
  onRowClick,
  pageSize = 50,
  enablePagination = true,
  serverSidePagination = false,
  totalRecords,
  currentPage: externalCurrentPage,
  onPageChange,
  dragDropHandlers,
  draggable = false,
  getRowStyle,
  getRowClassName,
  onRowMouseEnter,
  onRowMouseLeave,
  actions
}: TableProps) {
  const [openMenuRowId, setOpenMenuRowId] = useState<string | number | null>(null)
  const [internalCurrentPage, setInternalCurrentPage] = useState(1)
  const [showPaginationBar, setShowPaginationBar] = useState(false)

  // Close row action menu on outside click (works with `.table-action-wrapper` pattern)
  useEffect(() => {
    if (openMenuRowId === null) return

    const handleOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return

      // Keep open when clicking inside action wrapper or the portaled dropdown menu
      if (target.closest('.table-action-wrapper')) return
      if (target.closest('.table-action-dropdown')) return

      setOpenMenuRowId(null)
    }

    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [openMenuRowId])
  
  // Controlled vs uncontrolled pagination
  const currentPage = serverSidePagination ? (externalCurrentPage || 1) : internalCurrentPage
  const setCurrentPage = serverSidePagination ? (onPageChange || (() => {})) : setInternalCurrentPage

  const menuContext = useCallback(() => ({
    openMenuRowId,
    setOpenMenuRowId
  }), [openMenuRowId])

  // Pagination hesaplamaları
  const actualTotalRecords = serverSidePagination ? (totalRecords || 0) : data.length
  const totalPages = Math.ceil(actualTotalRecords / pageSize)
  const displayData = serverSidePagination 
    ? data // Server-side: backend already paginated
    : (enablePagination 
        ? data.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : data
      )

  // Hibrit PaginationBar visibility:
  // - Kısa sayfa (scroll yok) + totalPages > 1 → her zaman göster
  // - Uzun sayfa (scroll var) → scroll pozisyonuna göre göster/gizle
  useEffect(() => {
    if (!enablePagination || totalPages <= 1) {
      setShowPaginationBar(false)
      return
    }

    const panelContent = document.querySelector('.panel-content')
    if (!panelContent) return

    const checkVisibility = () => {
      const isScrollable = panelContent.scrollHeight > panelContent.clientHeight
      if (!isScrollable) {
        // Kısa sayfa → bar her zaman görünür
        setShowPaginationBar(true)
      } else {
        // Uzun sayfa → scroll'a göre
        setShowPaginationBar(panelContent.scrollTop > 200)
      }
    }

    // İlk kontrol (DOM render sonrası)
    const timer = setTimeout(checkVisibility, 50)

    panelContent.addEventListener('scroll', checkVisibility)
    const resizeObserver = new ResizeObserver(checkVisibility)
    resizeObserver.observe(panelContent)

    return () => {
      clearTimeout(timer)
      panelContent.removeEventListener('scroll', checkVisibility)
      resizeObserver.disconnect()
    }
  }, [enablePagination, totalPages, currentPage, data])

  // Sayfa değiştiğinde yukarı scroll (panel-content)
  const scrollToTop = () => {
    const panelContent = document.querySelector('.panel-content')
    if (panelContent) {
      panelContent.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Client-side pagination: data değiştiğinde sayfa 1'e dön
  // Server-side pagination'da bu gerekli değil, parent kontrol ediyor
  useEffect(() => {
    if (!serverSidePagination) {
      setInternalCurrentPage(1)
    }
  }, [data.length, serverSidePagination])

  if (loading) {
    return (
      <div className="table-container">
        <div className="table-loading">Yükleniyor...</div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="table-container">
        <div className="table-empty">{emptyMessage}</div>
      </div>
    )
  }

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th 
                key={column.key}
                className={`align-${column.align || 'left'}`}
                style={{ width: column.width }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayData.map((row, index) => {
            const isDragging = draggable && dragDropHandlers?.draggedItem?.[rowKey] === row[rowKey]
            const isDragOver = draggable && dragDropHandlers?.dragOverIndex === index
            const customRowStyle = getRowStyle?.(row, index) || {}
            const customRowClassName = getRowClassName?.(row, index) || ''
            
            return (
              <tr 
                key={row[rowKey] || index}
                draggable={draggable}
                onDragStart={draggable && dragDropHandlers ? (e) => dragDropHandlers.handleDragStart(e, row, index) : undefined}
                onDragEnd={draggable && dragDropHandlers ? dragDropHandlers.handleDragEnd : undefined}
                onDragOver={draggable && dragDropHandlers ? (e) => dragDropHandlers.handleDragOver(e, index) : undefined}
                onDragLeave={draggable && dragDropHandlers ? dragDropHandlers.handleDragLeave : undefined}
                onDrop={draggable && dragDropHandlers ? (e) => dragDropHandlers.handleDrop(e, index) : undefined}
                onClick={() => onRowClick?.(row)}
                onMouseEnter={() => onRowMouseEnter?.(row, index)}
                onMouseLeave={() => onRowMouseLeave?.(row, index)}
                className={`${onRowClick ? 'clickable' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''} ${customRowClassName}`}
                style={{
                  position: 'relative',
                  zIndex: 1000 - index,
                  opacity: isDragging ? 0.5 : 1,
                  transition: isDragging ? 'none' : 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isDragOver && !isDragging ? 'translateY(4px)' : 'translateY(0)',
                  ...customRowStyle
                }}
              >
                {columns.map((column, columnIndex) => {
                  const rowSpan = column.rowSpan ? column.rowSpan(row, index, displayData) : 1
                  if (rowSpan === 0) return null
                  const renderedCell = column.render
                    ? column.render(row[column.key], row, index, menuContext())
                    : row[column.key] || '-'
                  const actionItems = actions?.(row, index).filter(item => item.show !== false) || []
                  const shouldAppendActions = actionItems.length > 0 && columnIndex === columns.length - 1

                  return (
                    <td
                      key={column.key}
                      className={`align-${column.align || 'left'} ${column.className || ''}`}
                      style={{ width: column.width }}
                      rowSpan={rowSpan > 1 ? rowSpan : undefined}
                    >
                      {shouldAppendActions ? (
                        <div className="table-cell-with-actions">
                          <div className="table-cell-main">{renderedCell}</div>
                          <TableActionMenu
                            rowId={row[rowKey] || index}
                            openMenuRowId={openMenuRowId}
                            onOpenChange={setOpenMenuRowId}
                            items={actionItems}
                          />
                        </div>
                      ) : renderedCell}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* Pagination Bar */}
      {enablePagination && actualTotalRecords > pageSize && (
        <PaginationBar
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={actualTotalRecords}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onScrollTop={scrollToTop}
          visible={showPaginationBar}
        />
      )}
    </div>
  )
}
